import type { TtsVoice } from '@/api/endpoints/media'
import type { VoiceOption } from './VoiceCarousel'

/**
 * Avatar gradients keyed by TTS voice id. Purely cosmetic — paints the
 * carousel card background. Backend never returns these (they belong to
 * the UI), so we keep the catalog here and merge with the live voice
 * list at render time. Voices not in this map fall back to a neutral
 * gradient so adding a voice on the backend never breaks the carousel.
 */
const VOICE_GRADIENTS: Record<string, string> = {
  // PT-BR (Edge)
  'pt-BR-AntonioNeural': 'linear-gradient(135deg, #FF7A3D, #E8431A)',
  'pt-BR-FranciscaNeural': 'linear-gradient(135deg, #7C5CFF, #3B0F6B)',
  'pt-BR-ThalitaMultilingualNeural': 'linear-gradient(135deg, #DB2777, #500724)',
  // EN-US (Edge)
  'en-US-GuyNeural': 'linear-gradient(135deg, #FF7A3D, #E8431A)',
  'en-US-JennyNeural': 'linear-gradient(135deg, #7C5CFF, #3B0F6B)',
  'en-US-AriaNeural': 'linear-gradient(135deg, #DB2777, #500724)',
  'en-US-AndrewNeural': 'linear-gradient(135deg, #0891B2, #083344)',
  'en-US-AvaNeural': 'linear-gradient(135deg, #064E3B, #021816)',
  'en-US-BrianNeural': 'linear-gradient(135deg, #0B3D91, #041240)',
  'en-US-ChristopherNeural': 'linear-gradient(135deg, #92400E, #451A03)',
  'en-US-EmmaNeural': 'linear-gradient(135deg, #BE185D, #4C0519)',
  // Kokoro
  pf_dora: 'linear-gradient(135deg, #DB2777, #500724)',
  am_adam: 'linear-gradient(135deg, #0891B2, #083344)',
}

const FALLBACK_GRADIENT = 'linear-gradient(135deg, #475569, #1E293B)'

/**
 * Adapt a backend ``TtsVoice`` into the carousel's ``VoiceOption`` shape.
 * Maps the backend ``gender`` strings (which already arrive localised:
 * ``feminina`` / ``masculina`` in PT, ``male`` / ``female`` in EN) and
 * looks up a gradient — or falls back when the voice is new.
 */
export function toVoiceOption(voice: TtsVoice): VoiceOption {
  return {
    value: voice.value,
    name: voice.name,
    gender: voice.gender as VoiceOption['gender'],
    gradient: VOICE_GRADIENTS[voice.value] ?? FALLBACK_GRADIENT,
  }
}
